import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import {
  FolderOpen, Wrench, MessageSquare, Sparkles,
  Building2, ArrowRight, Loader2,
} from "lucide-react";

const fmt = (n) => (n === null || n === undefined ? "—" : String(n));

const ManagementCard = ({ icon: Icon, label, eyebrow, stats, meta, to, unread }) => (
  <Link
    to={to}
    className="group bg-[#0d0d0d] border border-white/6 hover:border-[#C5A059]/30 p-5 sm:p-6 flex flex-col gap-4 transition-all duration-250 hover:bg-[#111]"
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#C5A059]/10 flex items-center justify-center group-hover:bg-[#C5A059]/18 transition-colors duration-250 flex-shrink-0 rounded-sm">
          <Icon size={18} className="text-[#C5A059]" strokeWidth={1.5} />
        </div>
        <div>
          {eyebrow && (
            <p className="text-[10px] tracking-[0.2em] text-[#555] uppercase mb-0.5 font-medium">{eyebrow}</p>
          )}
          <h3 className="text-[#F5F5F7] text-[15px] font-medium">{label}</h3>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {unread > 0 && (
          <span className="bg-[#C5A059] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
            {unread} yeni
          </span>
        )}
        <ArrowRight size={15} className="text-[#333] group-hover:text-[#C5A059] group-hover:translate-x-0.5 transition-all duration-200" />
      </div>
    </div>

    {/* Stats row */}
    <div className="flex items-end gap-5 flex-wrap">
      {stats.map((s, i) => (
        <div key={i}>
          <p className="text-[#F5F5F7] text-3xl font-light leading-none tabular-nums">{fmt(s.value)}</p>
          <p className="text-[#555] text-[11px] mt-1.5 tracking-wide">{s.label}</p>
        </div>
      ))}
    </div>

    {/* Meta line */}
    {meta && (
      <p className="text-[#555] text-[12px] border-t border-white/5 pt-3 leading-relaxed line-clamp-1">
        {meta}
      </p>
    )}
  </Link>
);

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);

  useEffect(() => {
    const load = async () => {
      const [projRes, msgRes, drmRes] = await Promise.all([
        supabase.from("projects")
          .select("status, is_published, sort_order, title"),
        supabase.from("contact_messages")
          .select("id, name, message, is_read, is_archived, created_at")
          .eq("is_archived", false)
          .order("created_at", { ascending: false })
          .limit(1),
        supabase.from("dream_requests")
          .select("id, name, project_type, is_read, is_archived, created_at")
          .eq("is_archived", false)
          .order("created_at", { ascending: false })
          .limit(1),
      ]);

      const [msgCount, drmCount] = await Promise.all([
        supabase.from("contact_messages").select("id", { count: "exact", head: true }).eq("is_archived", false),
        supabase.from("dream_requests").select("id",   { count: "exact", head: true }).eq("is_archived", false),
      ]);
      const [msgUnread, drmUnread] = await Promise.all([
        supabase.from("contact_messages").select("id", { count: "exact", head: true }).eq("is_read", false).eq("is_archived", false),
        supabase.from("dream_requests").select("id",   { count: "exact", head: true }).eq("is_read", false).eq("is_archived", false),
      ]);

      const projs    = projRes.data || [];
      const featured = projs.find(p => p.sort_order === 1);

      setData({
        projects: {
          total:     projs.length,
          published: projs.filter(p => p.is_published).length,
          completed: projs.filter(p => p.status === "completed").length,
          ongoing:   projs.filter(p => p.status === "ongoing").length,
          planning:  projs.filter(p => p.status === "planning").length,
          featured:  featured?.title ?? null,
        },
        messages: {
          total:   msgCount.count ?? 0,
          unread:  msgUnread.count ?? 0,
          latest:  msgRes.data?.[0] ?? null,
        },
        dreams: {
          total:   drmCount.count ?? 0,
          unread:  drmUnread.count ?? 0,
          latest:  drmRes.data?.[0] ?? null,
        },
      });
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#555] text-sm mt-2">
        <Loader2 size={15} className="animate-spin text-[#C5A059]" />
        Yükleniyor...
      </div>
    );
  }

  const { projects: p, messages: m, dreams: d } = data;

  return (
    <div>
      <div className="mb-8">
        <p className="text-[#C5A059] text-[11px] tracking-[0.2em] uppercase mb-1 font-medium">Yönetim Paneli</p>
        <h1 className="text-[#F5F5F7] text-2xl font-light">Hoş Geldiniz</h1>
        <p className="text-[#555] text-[13px] mt-1">Sitenizi buradan yönetebilirsiniz.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ManagementCard
          icon={FolderOpen}
          eyebrow="İçerik"
          label="Projeler"
          to="/admin/projeler"
          stats={[
            { value: p.total,     label: "Toplam"       },
            { value: p.published, label: "Yayında"      },
            { value: p.completed, label: "Tamamlandı"   },
            { value: p.ongoing,   label: "Devam Ediyor" },
            { value: p.planning,  label: "Planlama"     },
          ]}
          meta={
            p.featured
              ? `Öne Çıkan: ${p.featured}`
              : "Öne çıkan proje atanmamış (sort_order = 1)"
          }
        />

        <ManagementCard
          icon={Wrench}
          eyebrow="İçerik"
          label="Hizmetler"
          to="/admin/hizmetler"
          stats={[
            { value: 4, label: "Hizmet" },
          ]}
          meta="İnşaat · Renovasyon · İç Mimari · Proje Yönetimi"
        />

        <ManagementCard
          icon={MessageSquare}
          eyebrow="İletişim"
          label="Mesajlar"
          to="/admin/mesajlar"
          unread={m.unread}
          stats={[
            { value: m.total,  label: "Toplam"      },
            { value: m.unread, label: "Okunmamış" },
          ]}
          meta={
            m.latest
              ? `Son: ${m.latest.name} — "${(m.latest.message || "").slice(0, 60)}${(m.latest.message || "").length > 60 ? "…" : ""}"`
              : "Henüz mesaj yok."
          }
        />

        <ManagementCard
          icon={Sparkles}
          eyebrow="Talepler"
          label="Hayalim Talepleri"
          to="/admin/hayalim-talepleri"
          unread={d.unread}
          stats={[
            { value: d.total,  label: "Toplam"      },
            { value: d.unread, label: "Okunmamış" },
          ]}
          meta={
            d.latest
              ? `Son: ${d.latest.name}${d.latest.project_type ? ` — ${d.latest.project_type}` : ""}`
              : "Henüz talep yok."
          }
        />

        <ManagementCard
          icon={Building2}
          eyebrow="Ayarlar"
          label="Şirket Bilgileri"
          to="/admin/sirket"
          stats={[
            { value: "Konyap", label: "Firma" },
          ]}
          meta="İletişim bilgileri · Sosyal medya · Logo"
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
